import raw from 'emoji-picker-react/dist/data/emojis-en.json';

/**
 * Emoji data for our own picker.
 *
 * Sourced from emoji-picker-react's dataset rather than a hand-maintained list
 * or a new dependency: the package is already installed, the file is plain JSON
 * keyed by category, and each entry carries both the codepoints and the search
 * names. We stopped using that package's COMPONENT — see EmojiGridPicker for
 * why — but its data is fine and there is no reason to ship a second copy of
 * every emoji on earth to avoid it.
 *
 * Entry shape is `{ n: [names], u: "1f600", a: "1" }`; `u` is hyphen-separated
 * hex codepoints for sequences (skin tones, flags, ZWJ joins).
 */

/** "1f1e9-1f1ea" -> 🇩🇪 */
export function emojiFromCodes(u) {
  if (typeof u !== 'string' || !u) return '';
  try {
    return String.fromCodePoint(...u.split('-').map((h) => parseInt(h, 16)));
  } catch {
    // A malformed codepoint must not take the whole picker down with it.
    return '';
  }
}

/**
 * Display order and labels. Deliberately explicit rather than
 * Object.keys(raw.emojis): that would surface `custom` (always empty here) and
 * would let a dataset update silently reorder the picker.
 */
export const EMOJI_CATEGORIES = [
  { id: 'smileys_people', label: 'Smileys & People', icon: '😀' },
  { id: 'animals_nature', label: 'Animals & Nature', icon: '🐻' },
  { id: 'food_drink', label: 'Food & Drink', icon: '🍔' },
  { id: 'travel_places', label: 'Travel & Places', icon: '🚌' },
  { id: 'activities', label: 'Activities', icon: '⚽' },
  { id: 'objects', label: 'Objects', icon: '💡' },
  { id: 'symbols', label: 'Symbols', icon: '❤️' },
  { id: 'flags', label: 'Flags', icon: '🏳️' },
];

/** `{ [categoryId]: [{ char, names }] }`, built once at module load. */
export const EMOJIS_BY_CATEGORY = EMOJI_CATEGORIES.reduce((acc, { id }) => {
  const list = Array.isArray(raw.emojis?.[id]) ? raw.emojis[id] : [];
  acc[id] = list
    .map((e) => ({ char: emojiFromCodes(e.u), names: Array.isArray(e.n) ? e.n : [] }))
    .filter((e) => e.char !== '');
  return acc;
}, {});

/**
 * Search across every category.
 *
 * Matches on the dataset's own names, which include both the short key
 * ("grin") and the full CLDR name ("grinning face"), so "grin" and "face" both
 * find 😀. Capped because an empty-ish query like "a" matches thousands and
 * rendering them all is what makes a picker feel broken.
 */
export function searchEmojis(query, limit = 120) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const out = [];
  for (const { id } of EMOJI_CATEGORIES) {
    for (const e of EMOJIS_BY_CATEGORY[id]) {
      if (e.names.some((n) => n.includes(q))) {
        out.push(e);
        if (out.length >= limit) return out;
      }
    }
  }
  return out;
}
